import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableContainer,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../store';
import {
  fetchUserRoleMappings,
  updateUserStatus,
  resetSuperAdminActionState,
  SuperAdminUserMapping
} from '../../../modules/superadmin';
import {
  selectUserRoleMappings,
  selectUserRoleMappingsLoading,
  selectCreateUserMappingSuccess,
  selectCreateUserMappingError,
  selectDeleteUserMappingSuccess,
  selectDeleteUserMappingError,
  selectUpdateUserMappingSuccess,
  selectUpdateUserMappingError,
  selectSuperAdminActionMessage
} from '../../../modules/superadmin/selectors';
import {
  MainContainer,
  HeaderContainer,
  PageTitle,
  StyledTableHead,
  StyledTableRow,
  StyledTableCell
} from '../styles';
import { useBreadcrumb } from '../../../context/BreadcrumbContext';
import { useSidebar } from '../../../context/SidebarContext';
import AlertPopup from '../../../components/common/AlertPopup';
import ConfirmationDialog from '../../../components/common/ConfirmationDialog';
import ActionPopup from '../../../components/common/ActionPopup';
import UserModal from './components/UserModal';
import UpdateRoleModal from './components/UpdateRoleModal';
import UserDetailDrawer from './components/UserDetailDrawer';

const UserRoleMapping: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { setActivePage } = useSidebar();
  const { setPath, setIsShow } = useBreadcrumb();

  // Redux State
  const mappings = useSelector(selectUserRoleMappings);
  const isLoading = useSelector(selectUserRoleMappingsLoading);
  const createSuccess = useSelector(selectCreateUserMappingSuccess);
  const deleteSuccess = useSelector(selectDeleteUserMappingSuccess);
  const updateSuccess = useSelector(selectUpdateUserMappingSuccess);
  const createError = useSelector(selectCreateUserMappingError);
  const deleteError = useSelector(selectDeleteUserMappingError);
  const updateError = useSelector(selectUpdateUserMappingError);
  const actionMessage = useSelector(selectSuperAdminActionMessage);

  const actionSuccess = createSuccess || deleteSuccess || updateSuccess;
  const actionError = createError || deleteError || updateError;

  // Local State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState<SuperAdminUserMapping | null>(null);
  const [alert, setAlert] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuUser, setMenuUser] = useState<SuperAdminUserMapping | null>(null);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [detailUserMapping, setDetailUserMapping] = useState<SuperAdminUserMapping | null>(null);

  // For activate (no remark needed)
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    userId: number;
  }>({ open: false, userId: 0 });

  // For deactivate/suspend (remark required) — uses existing ActionPopup
  const [actionPopup, setActionPopup] = useState<{
    open: boolean;
    userId: number;
    action: 'deactivate' | 'suspend';
    title: string;
    message: string;
  }>({ open: false, userId: 0, action: 'deactivate', title: '', message: '' });

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, mapping: SuperAdminUserMapping) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuUser(mapping);
  };

  const handleRowClick = (mapping: SuperAdminUserMapping) => {
    setDetailUserMapping(mapping);
    setDetailDrawerOpen(true);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuUser(null);
  };

  const handleStatusClick = (userId: number, action: 'activate' | 'deactivate' | 'suspend') => {
    handleMenuClose();
    if (action === 'activate') {
      setConfirmDialog({ open: true, userId });
    } else {
      const actionLabels: Record<string, string> = { deactivate: 'Deactivate', suspend: 'Suspend' };
      setActionPopup({
        open: true,
        userId,
        action,
        title: `${actionLabels[action]} User`,
        message: `Are you sure you want to ${action} this user?`,
      });
    }
  };

  const handleConfirmActivate = () => {
    dispatch(updateUserStatus({ user_id: confirmDialog.userId, action: 'activate' }));
    setConfirmDialog({ ...confirmDialog, open: false });
  };

  const handleActionSubmit = (remark?: string) => {
    dispatch(updateUserStatus({
      user_id: actionPopup.userId,
      action: actionPopup.action,
      remark: remark || undefined,
    }));
    setActionPopup({ ...actionPopup, open: false });
  };

  useEffect(() => {
    setActivePage('user-role-mapping');
    setPath([
      { title: 'Super Admin', path: '/super-admin' },
      { title: 'Super Admin Users', path: '/super-admin/user-role-mapping' },
    ]);
    setIsShow(true);
    dispatch(fetchUserRoleMappings());
  }, [dispatch, setActivePage, setPath, setIsShow]);

  useEffect(() => {
    if (actionSuccess) {
      setAlert({ message: actionMessage || 'Operation successful', severity: 'success' });
      setIsModalOpen(false);
      setIsUpdateModalOpen(false);
      setSelectedMapping(null);
      dispatch(fetchUserRoleMappings());
      dispatch(resetSuperAdminActionState());
    }
    if (actionError) {
      setAlert({ message: actionError, severity: 'error' });
      dispatch(resetSuperAdminActionState());
    }
  }, [actionSuccess, actionError, actionMessage, dispatch]);

  useEffect(() => {
    if (detailUserMapping && Array.isArray(mappings)) {
      const updatedMapping = mappings.find(m => m.mapping_id === detailUserMapping.mapping_id);
      if (updatedMapping) {
        setDetailUserMapping(updatedMapping);
      }
    }
  }, [mappings, detailUserMapping]);

  
  const handleRoleClick = (mapping: SuperAdminUserMapping) => {
    setSelectedMapping(mapping);
    setIsUpdateModalOpen(true);
  };

  return (
    <MainContainer>
      {alert && (
        <AlertPopup
          message={alert.message}
          severity={alert.severity}
          open={!!alert}
          onClose={() => setAlert(null)}
        />
      )}

      <HeaderContainer>
        <PageTitle>Super Admin Users</PageTitle>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsModalOpen(true)}
            sx={{
              textTransform: 'none',
              backgroundColor: '#5E8E7E',
              '&:hover': { backgroundColor: '#4A7A6A' }
            }}
          >
            Create Super Admin
          </Button>
        </Box>
      </HeaderContainer>

      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #eee', borderRadius: '8px' }}>
        <Table sx={{ minWidth: 650 }} aria-label="super admin users table">
          <StyledTableHead>
            <StyledTableRow>
              <StyledTableCell>ID</StyledTableCell>
              <StyledTableCell>Name</StyledTableCell>
              <StyledTableCell>Email</StyledTableCell>
              <StyledTableCell>Phone</StyledTableCell>
              <StyledTableCell>Role</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell align="right">Actions</StyledTableCell>
            </StyledTableRow>
          </StyledTableHead>
          <TableBody>
            {Array.isArray(mappings) && mappings.length > 0 ? (
              mappings.map((mapping) => (
                <StyledTableRow 
                  key={mapping.mapping_id}
                  onClick={() => handleRowClick(mapping)}
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#F9FAF9 !important' } }}
                >
                  <StyledTableCell>{mapping.mapping_id}</StyledTableCell>
                  <StyledTableCell>
                    {mapping.user.first_name} {mapping.user.last_name}
                  </StyledTableCell>
                  <StyledTableCell>{mapping.user.email}</StyledTableCell>
                  <StyledTableCell>{mapping.user.phone_number || 'N/A'}</StyledTableCell>
                  <StyledTableCell>
                    <Tooltip title="Click to update role">
                      <Chip 
                        label={mapping.system_role?.role_name || 'N/A'} 
                        size="small" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRoleClick(mapping);
                        }}
                        sx={{ 
                          backgroundColor: '#E8F5E9', 
                          color: '#2E7D32', 
                          fontWeight: 500,
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#C8E6C9' }
                        }}
                      />
                    </Tooltip>
                  </StyledTableCell>
                  <StyledTableCell>
                    {(() => {
                      if (mapping.user.is_suspended) {
                        return (
                          <Chip 
                            label="Suspended" 
                            size="small" 
                            color="error"
                            variant="outlined"
                            sx={{ fontWeight: 500 }}
                          />
                        );
                      }
                      if (!mapping.user.is_active) {
                        return (
                          <Chip 
                            label="Deactivated" 
                            size="small" 
                            color="default"
                            variant="outlined"
                            sx={{ fontWeight: 500 }}
                          />
                        );
                      }
                      return (
                        <Chip 
                          label="Active" 
                          size="small" 
                          color="success"
                          variant="outlined"
                          sx={{ fontWeight: 500 }}
                        />
                      );
                    })()}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    <Tooltip title="Actions">
                      <IconButton 
                        onClick={(e) => handleMenuOpen(e, mapping)}
                        sx={{ color: '#666' }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Tooltip>
                  </StyledTableCell>
                </StyledTableRow>
              ))
            ) : (
              <StyledTableRow>
                <StyledTableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  {isLoading ? <CircularProgress size={24} /> : 'No super admin users found.'}
                </StyledTableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 2,
          sx: { minWidth: 150, mt: 1 }
        }}
      >
        {menuUser && menuUser.user.is_active ? (
          <MenuItem onClick={() => handleStatusClick(menuUser.user.id, 'deactivate')}>
            Deactivate User
          </MenuItem>
        ) : (
          <MenuItem onClick={() => handleStatusClick(menuUser?.user.id as number, 'activate')}>
            Activate User
          </MenuItem>
        )}
        {menuUser && !menuUser.user.is_suspended && (
          <MenuItem onClick={() => handleStatusClick(menuUser.user.id, 'suspend')} sx={{ color: 'warning.main' }}>
            Suspend User
          </MenuItem>
        )}
      </Menu>

      <ConfirmationDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        onConfirm={handleConfirmActivate}
        title="Activate User"
        message="Are you sure you want to activate this user?"
      />

      <ActionPopup
        open={actionPopup.open}
        onClose={() => setActionPopup({ ...actionPopup, open: false })}
        onSubmit={handleActionSubmit}
        title={actionPopup.title}
        message={actionPopup.message}
        submitButtonText="Confirm"
        textFieldLabel="Reason"
        textFieldPlaceholder="Please provide a reason..."
        required
      />

      <UserModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />

      <UpdateRoleModal
        open={isUpdateModalOpen}
        onClose={() => setIsUpdateModalOpen(false)}
        mapping={selectedMapping}
      />

      <UserDetailDrawer
        open={detailDrawerOpen}
        onClose={() => setDetailDrawerOpen(false)}
        mapping={detailUserMapping}
      />
      {alert && (
        <AlertPopup
          open={Boolean(alert)}
          message={alert.message}
          severity={alert.severity}
          onClose={() => setAlert(null)}
        />
      )}
    </MainContainer>
  );
};

export default UserRoleMapping;
